import type { WishlistsState } from './wishlists';

interface UseWishlistLoadingStateDeps {
	wishlistsState?: Ref<WishlistsState | null>;
	wishlistIsFetching?: Ref<boolean | undefined>;
	loggedIn?: Ref<boolean>;
}

/**
 * Provides a reliable, shared source of truth for the wishlist loading state.
 *
 * This composable solves the problem of having different loading logic
 * scattered across components, ensuring consistent UI behavior.
 *
 * @param deps - Optional dependencies for testing
 * @returns Object containing reactive loading state computeds
 */
export function useWishlistLoadingState(deps?: UseWishlistLoadingStateDeps) {
	const wishlistsState =
		deps?.wishlistsState ?? useState<WishlistsState | null>('wishlists');
	const $wishlistIsFetching = (deps?.wishlistIsFetching ??
		useNuxtApp().$wishlistIsFetching) as Ref<boolean | undefined> | undefined;
	const loggedIn = deps?.loggedIn ?? useUserSession().loggedIn;

	/**
	 * Indicates if wishlist data is currently loading.
	 *
	 * $wishlistIsFetching states:
	 * - undefined: initial state, fetch hasn't started yet → loading
	 * - true: actively fetching → loading
	 * - false: fetch completed → not loading
	 *
	 * If $wishlistIsFetching ref itself is undefined (plugin not initialized),
	 * we return false to avoid permanent loading state.
	 */
	const isWishlistLoading = computed(() => {
		// On SSR, always show loading for pages that depend on this data
		// Skip this check during tests (import.meta.env.VITEST is set by Vitest)
		if (import.meta.server && !import.meta.env.VITEST) return true;

		// Not logged in users don't have wishlists
		if (!loggedIn.value) return false;

		// If the plugin didn't initialize the ref, don't show loading
		if (!$wishlistIsFetching) return false;

		// Show loading until fetch is explicitly completed (false)
		// undefined = initial state (loading), true = fetching (loading), false = done
		const isFetchPendingOrInProgress = $wishlistIsFetching.value !== false;
		return isFetchPendingOrInProgress;
	});

	/**
	 * Indicates if wishlist data has been successfully loaded and is ready for use.
	 */
	const isWishlistDataReady = computed(() => {
		if (!loggedIn.value) return false;
		// Data is ready when fetch has completed (explicitly false)
		return $wishlistIsFetching?.value === false;
	});

	return {
		isWishlistLoading,
		isWishlistDataReady,
		wishlistsState
	};
}
